"use client";

import React, { useCallback, useState } from "react";
import { useDropzone, Accept, FileRejection, ErrorCode } from "react-dropzone";
import {
  MdDelete,
  MdOutlineOndemandVideo,
  MdOutlinePictureAsPdf,
} from "react-icons/md";
import { toast } from "react-toastify";
import { useUserAuthDataContext } from "@/global-context/user-auth-context";
import { getMedia, MediaItem } from "@/lib/media/shared";
import MediaStateOverlay from "@/components/media-wrapper/media-state-overlay";
import styles from "./media.module.css";
import { DeleteConfirmationModal } from "./deleteModal";
import { useListingContext } from "../listingContex";
import { uploadListingMedia } from "../media-upload";
import { useScrollLock } from "@/hooks/use-scroll-lock";

// REMOVED: useBatchMediaPolling

export interface SingleMediaUploaderProps {
  category: "video" | "document";
  storeKey: "presentation_video" | "brochure";
  accept: Accept;
  maxSizeMB?: number;
}

export type LocalSingleMediaItem = MediaItem & { localUrl?: string };

export default function SingleMediaUploader({
  category,
  storeKey,
  accept,
  maxSizeMB = 100,
}: SingleMediaUploaderProps) {
  const { data, updateField } = useListingContext();
  const { userData } = useUserAuthDataContext();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [pendingItem, setPendingItem] = useState<LocalSingleMediaItem | null>(
    null,
  );
  const mediaItem = data?.[storeKey] as LocalSingleMediaItem | null;
  useScrollLock(showDeleteModal);

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    updateField(storeKey, null);
    setPendingItem(null);
    setShowDeleteModal(false);
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (!data) return;

      if (rejectedFiles.length > 0) {
        const error = rejectedFiles[0].errors[0];
        if (error.code === ErrorCode.FileTooLarge) {
          toast.error(`File is too large. Max size is ${maxSizeMB}MB.`);
        } else {
          toast.error("Invalid file format.");
        }
        return;
      }

      if (acceptedFiles.length === 0) return;
      const file = acceptedFiles[0];

      const tempMediaItem: LocalSingleMediaItem = {
        MediaId: `temp_${Date.now()}`,
        Status: "UPLOADED", // Immediately show uploading state
        OriginalFileName: file.name,
        MediaCategory: category,
        FileExtension: file.name.split(".").pop() || "",
        localUrl: URL.createObjectURL(file),
      };
      setPendingItem(tempMediaItem);

      try {
        const completedMediaItem = await uploadListingMedia(
          file,
          category,
          data.listing_id,
          userData.sub || "",
        );

        // Put it directly in the DB! No polling required!
        updateField(storeKey, {
          ...completedMediaItem,
          localUrl: tempMediaItem.localUrl, // Keep local preview running
        });

        // Clear pending because it's now in the global store
        setPendingItem(null);
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
        setPendingItem(null);
      }
    },
    [data, storeKey, category, maxSizeMB, userData.sub, updateField],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxFiles: 1,
    maxSize: maxSizeMB * 1024 * 1024,
  });

  const activeItem = pendingItem || mediaItem;

  if (!activeItem || !activeItem.MediaId) {
    return (
      <div
        {...getRootProps()}
        className={`${styles.singleDropzone} ${isDragActive ? styles.dragActive : ""}`}
      >
        <input {...getInputProps()} />
        {category === "video" ? (
          <MdOutlineOndemandVideo className={styles.dropzoneIcon} />
        ) : (
          <MdOutlinePictureAsPdf className={styles.dropzoneIcon} />
        )}
        <p className={styles.dropzoneText}>
          {isDragActive
            ? "Drop file here..."
            : `Click or drag to upload ${category}`}
        </p>
      </div>
    );
  }

  // If it's still 'UPLOADED', the overlay will hide if you configure it to only show on 'PROCESSING',
  // but for videos we usually want them playable if uploaded successfully.
  const showOverlay =
    activeItem.Status === "PROCESSING" || activeItem.Status === "FAILED";
  const resolvedMedia = activeItem.MediaId.startsWith("temp_")
    ? null
    : getMedia(activeItem, "lg");

  const displayUrl = resolvedMedia?.src || activeItem.localUrl || "";
  const posterUrl = resolvedMedia?.preview || "";

  return (
    <div
      className={`${styles.singlePreviewWrapper} ${category === "document" && styles.document}`}
    >
      <button
        type="button"
        className={styles.deleteBtn}
        onClick={handleDeleteClick}
      >
        <MdDelete size={20} />
      </button>

      {showOverlay && (
        <div style={{ position: "absolute", inset: 0, zIndex: 15 }}>
          <MediaStateOverlay status={activeItem.Status as any} theme="dark" />
        </div>
      )}

      {category === "video" && displayUrl && (
        <video
          src={displayUrl}
          poster={posterUrl}
          controls={!showOverlay}
          className={styles.videoElement}
        />
      )}

      {category === "document" && (
        <div className={styles.documentPreview}>
          <MdOutlinePictureAsPdf className={styles.documentIcon} />
          <span className={styles.documentName}>
            {activeItem.OriginalFileName || "Brochure.pdf"}
          </span>
        </div>
      )}

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title={`Delete ${category === "video" ? "Video" : "Document"}?`}
        description={`Are you sure you want to remove this ${category}? This action cannot be undone.`}
        confirmText="Delete File"
      />
    </div>
  );
}
